import React, { useState, useContext, useCallback, useMemo, useEffect } from "react";
import { format } from "date-fns";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import {
  IonLabel,
  IonItem,
  IonAvatar,
  IonList,
  IonSearchbar,
  IonIcon,
  IonBadge,
  useIonAlert,
  IonButton,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonSegment,
  IonSegmentButton,
} from "@ionic/react";
import "../../theme/AppStyles.css";

import { addOutline, chatboxOutline, createOutline, megaphoneOutline } from "ionicons/icons";

import { ProfileContext } from "../../context/ProfileContext";
import { AuthContext } from "../../context/AuthContext";
import { LoadingScreen } from "../../components/LoadingScreen";
import { WebLayout } from "../../components/layouts/WebLayout";
import { ChatPanel } from "../../components/desktop/ChatPanel";
import { JobChatPanel } from "../../components/desktop/JobChatPanel";
import { AnnouncementDetailPanel } from "../../components/desktop/AnnouncementDetailPanel";
import { AnnouncementEditorPanel } from "../../components/desktop/AnnouncementEditorPanel";

import { chatFunction } from "../../functions/Fx_Chats";
import { SUPPORT_MONKEY_ASSOCIATE, SUPPORT_MONKEY_UID } from "../../constants/supportConstants";
import { useChatListFilter } from "../../hooks/useChatListFilter";
import { useAnnouncementLists } from "../../hooks/useAnnouncementLists";
import { AnnouncementView } from "../../types/announcement";

const ChatsDesktop: React.FC = () => {
  const { currentUser, authReady } = useContext(AuthContext);
  const {
    myChats,
    myAnnouncements,
    myAnnouncementPosts,
    profile,
    myAssociates,
    allowedJobIdsScheduler,
    allowedJobIdsUnion,
    jobFilteringReady,
    jobRouteByJobId,
    cancelledBySchedulerWerker,
    cancelledBySchedulerScheduler,
  } = useContext(ProfileContext);
  
  const [newChatSearchText, setNewChatSearchText] = useState("");
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [commSegment, setCommSegment] = useState<"chats" | "announcements">("chats");
  const [announcementView, setAnnouncementView] = useState<AnnouncementView>("feed");
  const [announcementSearchText, setAnnouncementSearchText] = useState("");
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string | null>(null);
  const [announcementPanelMode, setAnnouncementPanelMode] = useState<"detail" | "create" | "edit">("detail");
  const [editingAnnouncementId, setEditingAnnouncementId] = useState<string | null>(null);
  const history = useHistory<any>();
  const location = useLocation();
  
  // Get chatId from URL query params
  const queryParams = new URLSearchParams(location.search);
  const initialChatId = queryParams.get("active");
  const [selectedChatId, setSelectedChatId] = useState<string | null>(initialChatId);

  const FALLBACK_AVATAR = "../assets/profilePic.png";
  const isSchedulerMode = profile?.UserLastRoleScheduler === true;

  // Update selected chat when URL changes
  useEffect(() => {
    const newChatId = queryParams.get("active");
    if (newChatId !== selectedChatId) {
      setSelectedChatId(newChatId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Use shared chat list filter hook
  const { filteredChats, searchText, setSearchText } = useChatListFilter({
    myChats,
    myAssociates,
    jobFilteringReady,
    allowedJobIdsUnion,
    cancelledBySchedulerWerker,
    cancelledBySchedulerScheduler,
  });

  const [presentAlert] = useIonAlert();

  const supportUnread = useMemo(() => {
    const row = (myChats || []).find((m: any) => String(m?.DMuid ?? "") === SUPPORT_MONKEY_UID);
    if (!row) return 0;
    return row?.IsRead === false ? 1 : 0;
  }, [myChats]);

  const {
    currentAnnouncementList,
    unreadAnnouncementsCount,
  } = useAnnouncementLists({
    myAnnouncements,
    myAnnouncementPosts,
    searchText: announcementSearchText,
    announcementView,
  });

  const unreadChatsCount = useMemo(() => {
    return (filteredChats || []).filter((msg: any) => msg?.IsRead === false).length + supportUnread;
  }, [filteredChats, supportUnread]);

  // Navigation
  const createChat = useCallback(() => {
    setNewChatSearchText("");
    setIsNewChatModalOpen(true);
  }, []);

  const handleChatSelect = useCallback((chatId: string) => {
    setSelectedChatId(chatId);
    // Update URL with selected chat
    history.replace(`/Chats?active=${chatId}`);
  }, [history]);

  const openCreateAnnouncement = useCallback(() => {
    setEditingAnnouncementId(null);
    setAnnouncementPanelMode("create");
  }, []);

  const openEditAnnouncement = useCallback((announcementId: string) => {
    setEditingAnnouncementId(announcementId);
    setAnnouncementPanelMode("edit");
  }, []);

  const handleAnnouncementSaved = useCallback((announcementId: string) => {
    setSelectedAnnouncementId(announcementId);
    setEditingAnnouncementId(announcementId);
    setAnnouncementPanelMode("detail");
    setAnnouncementView("mine");
  }, []);

  const handleAnnouncementDeleted = useCallback(() => {
    setSelectedAnnouncementId(null);
    setEditingAnnouncementId(null);
    setAnnouncementPanelMode("detail");
  }, []);

  const selectedChat = useMemo(() => {
    if (!selectedChatId) return null;
    return (myChats || []).find((chat: any) => chat?.Key === selectedChatId) || null;
  }, [myChats, selectedChatId]);

  const selectedIsJobChat = useMemo(() => {
    if (!selectedChat) return false;
    // Inline isJobChat logic: job chat has empty DMuid
    return typeof selectedChat?.DMuid === "string" && selectedChat.DMuid.trim() === "";
  }, [selectedChat]);

  const selectedJobMeta = useMemo(() => {
    if (!selectedChatId) return null;
    return jobRouteByJobId?.[selectedChatId] || null;
  }, [jobRouteByJobId, selectedChatId]);

  const selectedJobPanelId = useMemo(() => {
    if (!selectedChatId || !selectedIsJobChat) return null;
    return selectedJobMeta?.myJobDocId || selectedChatId;
  }, [selectedChatId, selectedIsJobChat, selectedJobMeta]);

  const selectedJobIsScheduler = useMemo(() => {
    if (!selectedChatId || !selectedIsJobChat) return false;
    
    // If we have a myJobDocId, this is definitely a werker view
    if (selectedJobMeta?.myJobDocId) return false;
    
    // Check if in scheduler jobs set
    return allowedJobIdsScheduler.has(selectedChatId);
  }, [allowedJobIdsScheduler, selectedChatId, selectedIsJobChat, selectedJobMeta]);

  const openSupportChat = async () => {
    presentAlert({
      header: "Customer Support",
      message: "Chat with WerkApp Customer Support Monkeys",
      buttons: [
        { text: "Cancel", role: "cancel" },
        {
          text: "Chat",
          handler: async () => {
            try {
              const result = await chatFunction(profile, SUPPORT_MONKEY_ASSOCIATE, "DM");
              handleChatSelect(result);
            } catch (error) {
              console.error("Error opening support chat:", error);
            }
          },
        },
      ],
    });
  };

  const filteredAssociatesForNewChat = useMemo(() => {
    const query = (newChatSearchText || "").trim().toLowerCase();

    return [...(myAssociates || [])]
      .filter((assoc: any) => {
        if (!assoc?.WerkerUid || assoc.WerkerUid === "nZhDwrvpfbRWoQz3P6VTdZR8nrp2") return false;
        if (!query) return true;

        const first = String(assoc.WerkerFirstName || "").toLowerCase();
        const last = String(assoc.WerkerLastName || "").toLowerCase();
        const company = String(assoc.WerkerCompany || "").toLowerCase();
        const occupation = String(assoc.Occupation || "").toLowerCase();

        return (
          first.includes(query) ||
          last.includes(query) ||
          company.includes(query) ||
          occupation.includes(query)
        );
      })
      .sort((a: any, b: any) => String(a.WerkerFirstName || "").localeCompare(String(b.WerkerFirstName || "")));
  }, [myAssociates, newChatSearchText]);

  const handleAssociateChatCreate = useCallback(
    async (assoc: any) => {
      try {
        const result = await chatFunction(profile, assoc, "DM");
        setIsNewChatModalOpen(false);
        setNewChatSearchText("");
        handleChatSelect(result);
      } catch (error) {
        console.error("Error creating or opening chat:", error);
      }
    },
    [profile, handleChatSelect],
  );

  if (!authReady) {
    return <LoadingScreen LoadingText="Loading" />;
  }

  if (currentUser.uid === "") {
    return <Redirect to={{ pathname: "/login", search: "?returnUrl=/portal" }} />;
  }

  const announcementsReady = Array.isArray(myAnnouncements);

  return (
    <WebLayout title="Communications">
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <IonModal
          isOpen={isNewChatModalOpen}
          onDidDismiss={() => setIsNewChatModalOpen(false)}
          style={{ '--width': 'min(640px, 92vw)', '--height': 'min(720px, 85vh)' } as React.CSSProperties}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                New Chat
              </IonTitle>
              <IonButtons slot="end">
                <IonButton
                  onClick={() => setIsNewChatModalOpen(false)}
                >
                  Close
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px 0' }}>
              <IonSearchbar
                value={newChatSearchText}
                onIonChange={(e) => setNewChatSearchText(e.detail.value || '')}
                animated
                placeholder="Search associates"
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              <IonList>
                {filteredAssociatesForNewChat.map((assoc: any) => (
                  <IonItem
                    key={assoc.WerkerUid}
                    button
                    onClick={() => handleAssociateChatCreate(assoc)}
                  >
                    <IonAvatar slot="start">
                      <img
                        src={assoc.WerkerProfilePicURL || FALLBACK_AVATAR}
                        alt=""
                        onError={(e: any) => {
                          e.target.src = FALLBACK_AVATAR;
                        }}
                      />
                    </IonAvatar>
                    <IonLabel>
                      <h2>{assoc.WerkerFirstName} {assoc.WerkerLastName}</h2>
                      <p>{assoc.Occupation || assoc.WerkerCompany || ''}</p>
                    </IonLabel>
                  </IonItem>
                ))}

                {filteredAssociatesForNewChat.length === 0 && (
                  <IonItem lines="none">
                    <IonLabel
                      style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--ion-color-medium)' }}
                    >
                      <h4 style={{ marginBottom: '6px' }}>No associates found</h4>
                      <p style={{ margin: 0 }}>Try a different search.</p>
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>
            </div>
          </div>
        </IonModal>

        <div
          style={{
            padding: "10px 12px 8px 12px",
            borderBottom: "1px solid var(--ion-color-light-shade)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <IonSegment
            value={commSegment}
            onIonChange={(e) => {
              const next = (e.detail.value as "chats" | "announcements") || "chats";
              setCommSegment(next);
              if (next === "announcements") {
                setAnnouncementPanelMode("detail");
              }
            }}
            style={{ maxWidth: 360 }}
          >
            <IonSegmentButton value="chats">
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <IonIcon icon={chatboxOutline} />
                <IonLabel>Chats</IonLabel>
                {unreadChatsCount > 0 && (
                  <IonBadge color="danger" style={{ fontSize: "0.7rem" }}>
                    {unreadChatsCount}
                  </IonBadge>
                )}
              </div>
            </IonSegmentButton>
            <IonSegmentButton value="announcements">
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <IonIcon icon={megaphoneOutline} />
                <IonLabel>Announcements</IonLabel>
                {unreadAnnouncementsCount > 0 && (
                  <IonBadge color="danger" style={{ fontSize: "0.7rem" }}>
                    {unreadAnnouncementsCount}
                  </IonBadge>
                )}
              </div>
            </IonSegmentButton>
          </IonSegment>

        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', width: '100%' }}>
        {/* Left Column - Chat List */}
        <div 
          style={{ 
            width: '50%',
            height: "100%", 
            overflowY: "auto", 
            padding: "0",
            borderRight: "1px solid var(--ion-color-light-shade)"
          }}
        >
            <IonSearchbar
              value={commSegment === "chats" ? searchText : announcementSearchText}
              onIonChange={(e) => {
                if (commSegment === "chats") {
                  setSearchText(e.detail.value || "");
                } else {
                  setAnnouncementSearchText(e.detail.value || "");
                }
              }}
              animated
              placeholder={commSegment === "chats" ? "Search chats" : "Search announcements"}
            />

            {commSegment === "chats" && (
              <div style={{ display: "flex", gap: 8, padding: "0 10px 8px 10px" }}>
                <IonButton fill="outline" size="small" onClick={createChat}>
                  <IonIcon slot="start" icon={createOutline} />
                  New Chat
                </IonButton>

                <IonButton
                  fill="outline"
                  size="small"
                  color={supportUnread > 0 ? "warning" : "medium"}
                  onClick={openSupportChat}
                >
                  Support
                  {supportUnread > 0 && (
                    <IonBadge color="danger" style={{ marginLeft: "4px" }}>
                      {supportUnread}
                    </IonBadge>
                  )}
                </IonButton>
              </div>
            )}

            {commSegment === "announcements" && isSchedulerMode && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 10px 8px 10px" }}>
                <IonSegment
                  value={announcementView}
                  onIonChange={(e) => setAnnouncementView((e.detail.value as AnnouncementView) || "feed")}
                  style={{ width: 180 }}
                >
                  <IonSegmentButton value="feed">
                    <IonLabel>Feed</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="mine">
                    <IonLabel>Sent</IonLabel>
                  </IonSegmentButton>
                </IonSegment>

                <IonButton size="small" color="warning" onClick={openCreateAnnouncement}>
                  <IonIcon slot="start" icon={addOutline} />
                  New
                </IonButton>
              </div>
            )}

            {commSegment === "chats" && (
              <IonList
              >
                {filteredChats.map((chat: any) => {
                  const isSelected = selectedChatId === chat.Key;
                  const dateStr = chat._dateMs
                    ? format(new Date(chat._dateMs), "M/d/yy h:mm a")
                    : "";

                  return (
                    <IonItem
                      key={chat.Key}
                      button
                      onClick={() => handleChatSelect(chat.Key)}
                      color={isSelected ? "light" : undefined}
                      style={{
                        borderLeft: isSelected ? "4px solid var(--ion-color-primary)" : "4px solid transparent",
                        fontWeight: isSelected ? "600" : "normal",
                      }}
                    >
                      <IonAvatar
                        slot="start"
                      >
                        <img
                          src={chat.SendersPic || FALLBACK_AVATAR}
                          alt=""
                          onError={(e: any) => {
                            e.target.src = FALLBACK_AVATAR;
                          }}
                        />
                      </IonAvatar>
                      <IonLabel
                      >
                        <h2 style={{ fontWeight: chat.IsRead ? 400 : 700 }}>
                          {chat.MessageTitle}
                          {chat._kind === "JOB" && (
                            <IonBadge
                              color="medium"
                              style={{ marginLeft: "6px", fontSize: "0.65rem" }}
                            >
                              JOB
                            </IonBadge>
                          )}
                        </h2>
                        <p
                          style={{
                            fontWeight: chat.IsRead ? 300 : 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chat._displayPreview}
                        </p>
                        <p style={{ fontSize: "0.75rem", opacity: 0.7 }}>{dateStr}</p>
                      </IonLabel>
                      {!chat.IsRead && (
                        <IonBadge
                          slot="end"
                          color="danger"
                        >
                          •
                        </IonBadge>
                      )}
                    </IonItem>
                  );
                })}

                {filteredChats.length === 0 && (
                  <IonItem
                    lines="none"
                  >
                    <IonLabel
                      style={{ textAlign: "center", padding: "40px 20px" }}
                    >
                      <h4>No chats yet</h4>
                      <p style={{ opacity: 0.7 }}>Start a new conversation</p>
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>
            )}

            {commSegment === "announcements" && (
              <IonList>
                {!announcementsReady && (
                  <IonItem lines="none">
                    <IonLabel style={{ textAlign: "center", padding: "40px 20px" }}>
                      <h4>Loading announcements...</h4>
                    </IonLabel>
                  </IonItem>
                )}

                {announcementsReady && currentAnnouncementList.length === 0 && (
                  <IonItem lines="none">
                    <IonLabel style={{ textAlign: "center", padding: "40px 20px" }}>
                      <h4>{announcementView === "feed" ? "No announcements yet" : "No sent announcements"}</h4>
                    </IonLabel>
                  </IonItem>
                )}

                {announcementsReady &&
                  currentAnnouncementList.map((item: any) => {
                    const isSelected = selectedAnnouncementId === item.announcementId;
                    const unread = item?.isRead === false;
                    const preview = announcementView === "mine" ? String(item?.body || "") : String(item?.bodyPreview || "");

                    return (
                      <IonItem
                        key={item.announcementId}
                        button
                        color={isSelected ? "light" : undefined}
                        onClick={() => {
                          setSelectedAnnouncementId(item.announcementId);
                          setAnnouncementPanelMode("detail");
                          setEditingAnnouncementId(item.announcementId);
                        }}
                        style={{
                          borderLeft: unread
                            ? "4px solid var(--ion-color-warning)"
                            : isSelected
                            ? "4px solid var(--ion-color-primary)"
                            : "4px solid transparent",
                          border: "1px solid var(--ion-color-step-250, rgba(255,255,255,0.18))",
                          boxShadow: "inset 0 0 0 1px var(--ion-color-step-200, rgba(255,255,255,0.10))",
                          margin: "8px 10px",
                          borderRadius: "12px",
                        }}
                      >
                        <IonAvatar slot="start">
                          <img src={item?.authorProfilePicURL || FALLBACK_AVATAR} alt="" />
                        </IonAvatar>
                        <IonLabel>
                          <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {item?.title || "Untitled"}
                          </h2>
                          <p
                            style={{
                              marginBottom: 4,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {preview}
                          </p>

                          {!!item?.imageUrl && (
                            <div style={{ marginBottom: 6 }}>
                              <img
                                src={item.imageUrl}
                                alt="Announcement"
                                style={{
                                  width: "100%",
                                  maxHeight: "240px",
                                  objectFit: "contain",
                                  background: "rgba(0,0,0,0.04)",
                                  borderRadius: "10px",
                                  display: "block",
                                }}
                                onError={(e) => {
                                  const el = e.currentTarget as HTMLImageElement;
                                  el.style.display = "none";
                                }}
                              />
                            </div>
                          )}

                          <p style={{ fontSize: "0.75rem", opacity: 0.72 }}>
                            {item?.authorName || "Unknown"}
                            {item?.createdAt ? ` • ${format(new Date(Number(item.createdAt)), "M/d h:mm a")}` : ""}
                          </p>
                        </IonLabel>
                        {unread && <IonBadge slot="end" color="danger">•</IonBadge>}
                      </IonItem>
                    );
                  })}
              </IonList>
            )}
        </div>

        {/* Right Column - Chat Panel */}
        <div 
          style={{ 
            width: '50%',
            height: "100%", 
            padding: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          {commSegment === "chats" && (
            <>
              {selectedIsJobChat ? (
                <JobChatPanel
                  jobId={selectedJobPanelId}
                  chatId={selectedChatId}
                  isScheduler={selectedJobIsScheduler}
                />
              ) : (
                <ChatPanel chatId={selectedChatId} />
              )}
            </>
          )}

          {commSegment === "announcements" && announcementPanelMode === "detail" && (
            <AnnouncementDetailPanel
              announcementId={selectedAnnouncementId}
              onEditRequest={openEditAnnouncement}
              onDeleted={handleAnnouncementDeleted}
            />
          )}

          {commSegment === "announcements" && (announcementPanelMode === "create" || announcementPanelMode === "edit") && (
            <AnnouncementEditorPanel
              mode={announcementPanelMode === "create" ? "create" : "edit"}
              announcementId={announcementPanelMode === "edit" ? editingAnnouncementId : null}
              onCancel={() => {
                setAnnouncementPanelMode("detail");
                if (announcementPanelMode === "create") {
                  setEditingAnnouncementId(null);
                }
              }}
              onSaved={handleAnnouncementSaved}
            />
          )}
        </div>
        </div>
      </div>
    </WebLayout>
  );
};

export default ChatsDesktop;
